import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  Users,
  Plus,
  Download,
  Edit,
  Trash2,
  Eye,
  UserPlus,
  GraduationCap,
  Mail,
  Phone,
  Settings,
  BookOpen,
  UserCheck,
  Calendar,
  Filter,
  X,
} from "lucide-react";
import {
  getTeachers,
  getTeacherStats,
  deleteTeacher,
} from "../../services/teachers";
import { getDepartments } from "../../services/departments";
import { getCourses } from "../../services/courses";
import { TeacherWithUserInfo } from "../../types/teacher";
import TeacherModal from "../../components/forms/TeacherModal";
import TeacherDetailsModal from "../../components/modals/TeacherDetailsModal";
import ClassAssignmentModal from "../../components/modals/ClassAssignmentModal";
import StatusChangeModal from "../../components/modals/StatusChangeModal";
import LeaveRequestsModal from "../../components/modals/LeaveRequestsModal";
import Pagination from "../../components/common/Pagination";
import { useHorizontalDragScroll } from "../../hooks/useHorizontalDragScroll";

const AdminTeachers: React.FC = () => {
  const queryClient = useQueryClient();
  const tableScrollRef = useHorizontalDragScroll<HTMLDivElement>();
  const [searchTerm, setSearchTerm] = useState("");
  const [activeSearchTerm, setActiveSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "active" | "inactive" | "suspended" | "pending"
  >("all");
  const [departmentFilter, setDepartmentFilter] = useState<string>("all");
  const [courseFilter, setCourseFilter] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showFilters, setShowFilters] = useState(false);

  // Handle Enter key press to trigger search
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setActiveSearchTerm(searchTerm);
      setCurrentPage(1);
    }
  };

  // Modal states
  const [isTeacherModalOpen, setIsTeacherModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedTeacherId, setSelectedTeacherId] = useState<string | null>(
    null,
  );
  const [editingTeacherId, setEditingTeacherId] = useState<string | null>(null);

  // Management modal states
  const [isManagementModalOpen, setIsManagementModalOpen] = useState(false);
  const [selectedTeacherForManagement, setSelectedTeacherForManagement] =
    useState<string | null>(null);
  const [isClassAssignmentModalOpen, setIsClassAssignmentModalOpen] =
    useState(false);
  const [isStatusChangeModalOpen, setIsStatusChangeModalOpen] = useState(false);
  const [isLeaveRequestModalOpen, setIsLeaveRequestModalOpen] = useState(false);

  // Handle page size change
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  // Fetch teachers data
  const {
    data: teachersResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      "teachers",
      currentPage,
      pageSize,
      activeSearchTerm,
      statusFilter,
      departmentFilter,
      courseFilter,
    ],
    queryFn: () =>
      getTeachers({
        page: currentPage,
        limit: pageSize,
        search: activeSearchTerm || undefined,
        status: statusFilter === "all" ? undefined : statusFilter,
        department: departmentFilter === "all" ? undefined : departmentFilter,
        course: courseFilter === "all" ? undefined : courseFilter,
      }),
  });

  // Fetch teacher statistics
  const { data: statsResponse } = useQuery({
    queryKey: ["teacherStats"],
    queryFn: () => getTeacherStats(),
  });

  // Fetch departments for lookup
  const { data: departmentsResponse } = useQuery({
    queryKey: ["departments-lookup"],
    queryFn: () => getDepartments(1, 1000, undefined, undefined, true), // Get all departments
  });

  // Fetch courses for filter dropdown
  const { data: coursesResponse } = useQuery({
    queryKey: ["courses-lookup"],
    queryFn: () => getCourses({ limit: 1000 }), // Get all courses
  });

  // Delete teacher mutation
  const deleteTeacherMutation = useMutation({
    mutationFn: deleteTeacher,
    onSuccess: () => {
      toast.success("Teacher deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["teachers"] });
      queryClient.invalidateQueries({ queryKey: ["teacherStats"] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Failed to delete teacher");
    },
  });

  const teachers: TeacherWithUserInfo[] = teachersResponse?.data || [];
  const pagination = teachersResponse?.pagination;
  const stats = statsResponse?.data;
  const departments = departmentsResponse?.data || [];
  const courses = coursesResponse?.data || [];

  // Create department lookup map
  const departmentMap = departments.reduce(
    (map, dept) => {
      map[dept._id] = dept.name;
      return map;
    },
    {} as Record<string, string>,
  );

  // Create course lookup map
  const courseMap = courses.reduce(
    (map, course) => {
      map[course._id] = course.name;
      return map;
    },
    {} as Record<string, string>,
  );

  // Helper function to get status color and text
  const getStatusDisplay = (teacher: any) => {
    // Check for explicit status field first, then fallback to isActive
    const status =
      teacher.userInfo?.status || (teacher.isActive ? "active" : "inactive");

    switch (status) {
      case "active":
        return {
          color: "bg-green-100 text-green-800",
          text: "Active",
        };
      case "inactive":
        return {
          color: "bg-red-100 text-red-800",
          text: "Inactive",
        };
      case "suspended":
        return {
          color: "bg-orange-100 text-orange-800",
          text: "Suspended",
        };
      case "pending":
        return {
          color: "bg-yellow-100 text-yellow-800",
          text: "Pending",
        };
      default:
        return {
          color: "bg-gray-100 text-gray-800",
          text: "Unknown",
        };
    }
  };

  const handleDeleteTeacher = async (teacherId: string) => {
    if (!confirm("Are you sure you want to delete this teacher?")) return;
    deleteTeacherMutation.mutate(teacherId);
  };

  const handleEditTeacher = (teacherId: string) => {
    setEditingTeacherId(teacherId);
    setIsTeacherModalOpen(true);
  };

  const handleViewTeacher = (teacherId: string) => {
    setSelectedTeacherId(teacherId);
    setIsDetailsModalOpen(true);
  };

  const handleAddTeacher = () => {
    setEditingTeacherId(null);
    setIsTeacherModalOpen(true);
  };

  const handleCloseTeacherModal = () => {
    setIsTeacherModalOpen(false);
    setEditingTeacherId(null);
  };

  const handleCloseDetailsModal = () => {
    setIsDetailsModalOpen(false);
    setSelectedTeacherId(null);
  };

  const handleEditFromDetails = () => {
    if (selectedTeacherId) {
      setEditingTeacherId(selectedTeacherId);
      setIsDetailsModalOpen(false);
      setIsTeacherModalOpen(true);
    }
  };

  const handleExport = () => {
    console.log("Export teachers data");
  };

  // Management functions
  const openManagementModal = (teacherId: string) => {
    setSelectedTeacherForManagement(teacherId);
    setIsManagementModalOpen(true);
  };

  const handleManageClasses = (teacherId: string) => {
    setSelectedTeacherId(teacherId);
    setIsClassAssignmentModalOpen(true);
    setIsManagementModalOpen(false);
  };

  const handleChangeStatus = (teacherId: string) => {
    setSelectedTeacherId(teacherId);
    setIsStatusChangeModalOpen(true);
    setIsManagementModalOpen(false);
  };

  const handleLeaveRequests = (teacherId: string) => {
    setSelectedTeacherId(teacherId);
    setIsLeaveRequestModalOpen(true);
    setIsManagementModalOpen(false);
  };

  const handleViewDetails = (teacherId: string) => {
    setSelectedTeacherId(teacherId);
    setIsDetailsModalOpen(true);
    setIsManagementModalOpen(false);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-300 rounded-xl w-1/3"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-slate-300 rounded-2xl"></div>
            ))}
          </div>
          <div className="h-96 bg-slate-300 rounded-2xl"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-12">
          <div className="text-center">
            <div className="bg-gradient-to-br from-red-300 to-red-400 rounded-2xl p-4 w-16 h-16 mx-auto shadow-lg flex items-center justify-center mb-4">
              <GraduationCap className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-lg font-bold text-slate-900">
              Error loading teachers
            </h3>
            <p className="mt-2 text-slate-600 font-medium">
              Please try refreshing the page or contact support.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6 space-y-6">
      {/* Header */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-gradient-to-br from-emerald-300 to-emerald-400 rounded-2xl p-3 shadow-lg">
              <GraduationCap className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">
                Teachers Management
              </h1>
              <p className="text-slate-600 font-medium mt-1">
                Manage teachers and their assignments
              </p>
            </div>
          </div>
          <button
            onClick={handleAddTeacher}
            className="bg-white border-2 border-slate-200 px-5 py-2.5 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2"
          >
            <Plus className="h-5 w-5 text-emerald-500" />
            <span className="text-slate-700">Add Teacher</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-bold text-slate-900">Total Teachers</p>
              <p className="text-3xl font-bold text-slate-900 mt-2">
                {stats?.totalTeachers || teachers.length}
              </p>
            </div>
            <div className="bg-gradient-to-br from-blue-300 to-indigo-400 rounded-2xl p-4 shadow-lg">
              <Users className="h-8 w-8 text-white" />
            </div>
          </div>
        </div>
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-bold text-slate-900">
                Active Teachers
              </p>
              <p className="text-3xl font-bold text-slate-900 mt-2">
                {stats?.activeTeachers ||
                  teachers.filter((t) => t.isActive).length}
              </p>
            </div>
            <div className="bg-gradient-to-br from-emerald-300 to-emerald-400 rounded-2xl p-4 shadow-lg">
              <UserPlus className="h-8 w-8 text-white" />
            </div>
          </div>
        </div>
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-bold text-slate-900">Departments</p>
              <p className="text-3xl font-bold text-slate-900 mt-2">
                {Array.from(new Set(teachers.map((t) => t.department))).length}
              </p>
            </div>
            <div className="bg-gradient-to-br from-purple-300 to-purple-400 rounded-2xl p-4 shadow-lg">
              <GraduationCap className="h-8 w-8 text-white" />
            </div>
          </div>
        </div>
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-bold text-slate-900">
                Avg. Experience
              </p>
              <p className="text-3xl font-bold text-slate-900 mt-2">
                {stats?.averageExperience || 0}y
              </p>
            </div>
            <div className="bg-gradient-to-br from-orange-300 to-orange-400 rounded-2xl p-4 shadow-lg">
              <Eye className="h-8 w-8 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
        <div className="flex flex-col gap-4">
          {/* Top row: Search and action buttons */}
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
            <div className="flex-1 max-w-2xl">
              <input
                type="text"
                placeholder="Search teachers... (Press Enter)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl font-medium shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
              />
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-4 py-2.5 border-2 rounded-xl font-bold shadow-sm hover:shadow-lg transition-all duration-300 flex items-center gap-2 ${
                  showFilters ||
                  statusFilter !== "all" ||
                  departmentFilter !== "all" ||
                  courseFilter !== "all"
                    ? "bg-blue-50 border-blue-300 text-blue-700"
                    : "bg-white border-slate-200 text-slate-700"
                }`}
              >
                {showFilters ? (
                  <X className="h-5 w-5" />
                ) : (
                  <Filter className="h-5 w-5" />
                )}
                <span className="hidden sm:inline">
                  {showFilters ? "Close" : "Filters"}
                </span>
                {(statusFilter !== "all" ||
                  departmentFilter !== "all" ||
                  courseFilter !== "all") &&
                  !showFilters && (
                    <span className="bg-blue-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                      {
                        [
                          statusFilter !== "all",
                          departmentFilter !== "all",
                          courseFilter !== "all",
                        ].filter(Boolean).length
                      }
                    </span>
                  )}
              </button>
              <button
                onClick={handleExport}
                className="bg-white border-2 border-slate-200 px-4 py-2.5 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2"
              >
                <Download className="h-5 w-5 text-slate-500" />
                <span className="hidden sm:inline text-slate-700">Export</span>
              </button>
            </div>
          </div>

          {/* Expandable filters section */}
          {showFilters && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pt-3 border-t border-slate-200 animate-slide-down">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) =>
                    setStatusFilter(
                      e.target.value as
                        | "all"
                        | "active"
                        | "inactive"
                        | "suspended"
                        | "pending",
                    )
                  }
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl font-medium shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Department
                </label>
                <select
                  value={departmentFilter}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl font-medium shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                >
                  <option value="all">All Departments</option>
                  {departments.map((dept) => (
                    <option key={dept._id} value={dept._id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Course
                </label>
                <select
                  value={courseFilter}
                  onChange={(e) => setCourseFilter(e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl font-medium shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                >
                  <option value="all">All Courses</option>
                  {courses.map((course) => (
                    <option key={course._id} value={course._id}>
                      {course.name} ({course.level})
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2 lg:col-span-3 flex justify-end">
                <button
                  onClick={() => {
                    setStatusFilter("all");
                    setDepartmentFilter("all");
                    setCourseFilter("all");
                  }}
                  className="text-sm font-bold text-slate-600 hover:text-slate-900 transition-colors"
                >
                  Clear all filters
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Teachers Table */}
      <div
        className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl overflow-hidden"
        style={{ minHeight: "50vh", overflow: "visible" }}
      >
        <div
          ref={tableScrollRef}
          className="overflow-x-auto h-full"
          style={{ overflow: "auto visible" }}
        >
          <table className="min-w-full divide-y divide-slate-200 h-full">
            <thead className="bg-gradient-to-r from-slate-50 to-blue-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-900 uppercase tracking-wider">
                  Teacher
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-900 uppercase tracking-wider">
                  Department
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-900 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-900 uppercase tracking-wider">
                  Hire Date
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-900 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {teachers.map((teacher) => (
                <tr
                  key={teacher._id}
                  className="hover:bg-gradient-to-r hover:from-slate-50 hover:to-blue-50 transition-all duration-200"
                  style={{ overflow: "visible" }}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-11 w-11">
                        {teacher.userInfo?.profileDetails?.profilePicture ? (
                          <img
                            src={teacher.userInfo.profileDetails.profilePicture}
                            alt={`${teacher.userInfo?.firstName} ${teacher.userInfo?.lastName}`}
                            className="h-11 w-11 rounded-xl object-cover shadow-md"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display =
                                "none";
                              (
                                e.target as HTMLImageElement
                              ).nextElementSibling?.classList.remove("hidden");
                            }}
                          />
                        ) : null}
                        <div
                          className={`h-11 w-11 rounded-xl bg-gradient-to-br from-emerald-300 to-emerald-400 flex items-center justify-center shadow-md ${
                            teacher.userInfo?.profileDetails?.profilePicture
                              ? "hidden"
                              : ""
                          }`}
                        >
                          <span className="text-white font-bold text-sm">
                            {teacher.userInfo?.firstName?.charAt(0) || ""}
                            {teacher.userInfo?.lastName?.charAt(0) || ""}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-bold text-slate-900">
                          {teacher.userInfo?.firstName || ""}{" "}
                          {teacher.userInfo?.lastName || ""}
                        </div>
                        <div className="text-xs text-slate-500 font-medium">
                          {teacher.employeeId || teacher._id}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-900">
                    {departmentMap[teacher.department] || teacher.department}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {(() => {
                      const statusDisplay = getStatusDisplay(teacher);
                      const gradientColors = {
                        "bg-green-100 text-green-800":
                          "bg-gradient-to-br from-emerald-300 to-emerald-400 text-white",
                        "bg-red-100 text-red-800":
                          "bg-gradient-to-br from-red-300 to-red-400 text-white",
                        "bg-orange-100 text-orange-800":
                          "bg-gradient-to-br from-orange-300 to-orange-400 text-white",
                        "bg-yellow-100 text-yellow-800":
                          "bg-gradient-to-br from-amber-300 to-yellow-400 text-white",
                        "bg-gray-100 text-gray-800":
                          "bg-gradient-to-br from-slate-300 to-slate-400 text-white",
                      };
                      return (
                        <span
                          className={`inline-flex px-3 py-1.5 text-xs font-bold rounded-xl shadow-sm ${
                            gradientColors[
                              statusDisplay.color as keyof typeof gradientColors
                            ] || statusDisplay.color
                          }`}
                        >
                          {statusDisplay.text}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-900">
                    {teacher.joiningDate
                      ? new Date(teacher.joiningDate).toLocaleDateString()
                      : "N/A"}
                  </td>
                  <td
                    className="px-6 py-4 whitespace-nowrap text-sm font-medium relative"
                    style={{ overflow: "visible" }}
                  >
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleViewTeacher(teacher._id)}
                        className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
                        title="View Details"
                      >
                        <Eye className="h-4 w-4 text-blue-500" />
                      </button>
                      <button
                        onClick={() => handleEditTeacher(teacher._id)}
                        className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4 text-amber-500" />
                      </button>
                      <button
                        onClick={() => handleDeleteTeacher(teacher._id)}
                        className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
                        title="Delete"
                        disabled={deleteTeacherMutation.isPending}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </button>

                      {/* Management Button */}
                      <button
                        onClick={() => openManagementModal(teacher._id)}
                        className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
                        title="Manage Teacher"
                      >
                        <Settings className="h-4 w-4 text-purple-500" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Spacer to ensure dropdown visibility */}
          <div className="h-24 w-full"></div>
        </div>

        {teachers.length === 0 && (
          <div className="text-center py-12">
            <div className="bg-gradient-to-br from-slate-300 to-slate-400 rounded-2xl p-4 w-16 h-16 mx-auto shadow-lg flex items-center justify-center mb-4">
              <GraduationCap className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-lg font-bold text-slate-900">
              No teachers found
            </h3>
            <p className="mt-2 text-slate-600 font-medium">
              Try adjusting your search criteria or add a new teacher.
            </p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination && (
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
          <Pagination
            currentPage={currentPage}
            totalPages={pagination.totalPages}
            onPageChange={setCurrentPage}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
      )}

      {/* Modals */}
      <TeacherModal
        isOpen={isTeacherModalOpen}
        onClose={handleCloseTeacherModal}
        teacherId={editingTeacherId || undefined}
      />

      {selectedTeacherId && (
        <TeacherDetailsModal
          isOpen={isDetailsModalOpen}
          onClose={handleCloseDetailsModal}
          teacherId={selectedTeacherId}
          onEdit={handleEditFromDetails}
        />
      )}

      {/* Management Actions Modal */}
      {isManagementModalOpen && selectedTeacherForManagement && (
        <div className="fixed inset-0 z-[9999] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity z-[9998]"
              onClick={() => setIsManagementModalOpen(false)}
            ></div>

            <div className="relative inline-block align-bottom bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-2xl text-left overflow-hidden transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full z-[10000]">
              <div className="bg-white px-6 pt-6 pb-5">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-xl bg-gradient-to-br from-purple-300 to-purple-400 shadow-lg sm:mx-0 sm:h-10 sm:w-10">
                    <Settings className="h-6 w-6 text-white" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                    <h3 className="text-lg leading-6 font-bold text-slate-900">
                      Manage Teacher
                    </h3>
                    <div className="mt-4">
                      <p className="text-sm text-slate-600 font-medium mb-4">
                        Choose an action to perform on this teacher:
                      </p>

                      <div className="space-y-2">
                        <button
                          onClick={() =>
                            handleManageClasses(selectedTeacherForManagement)
                          }
                          className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-medium bg-gradient-to-r from-slate-50 to-blue-50 hover:shadow-md rounded-xl transition-all border border-slate-200/60"
                        >
                          <div className="bg-gradient-to-br from-blue-300 to-blue-400 rounded-lg p-2 shadow-sm">
                            <BookOpen className="h-5 w-5 text-white" />
                          </div>
                          <div>
                            <div className="font-bold text-slate-900">
                              Assign Classes
                            </div>
                            <div className="text-xs text-slate-600 font-medium">
                              Manage class and subject assignments
                            </div>
                          </div>
                        </button>

                        <button
                          onClick={() =>
                            handleChangeStatus(selectedTeacherForManagement)
                          }
                          className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-medium bg-gradient-to-r from-slate-50 to-blue-50 hover:shadow-md rounded-xl transition-all border border-slate-200/60"
                        >
                          <div className="bg-gradient-to-br from-emerald-300 to-emerald-400 rounded-lg p-2 shadow-sm">
                            <UserCheck className="h-5 w-5 text-white" />
                          </div>
                          <div>
                            <div className="font-bold text-slate-900">
                              Change Status
                            </div>
                            <div className="text-xs text-slate-600 font-medium">
                              Update teacher's active status
                            </div>
                          </div>
                        </button>

                        <button
                          onClick={() =>
                            handleLeaveRequests(selectedTeacherForManagement)
                          }
                          className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-medium bg-gradient-to-r from-slate-50 to-blue-50 hover:shadow-md rounded-xl transition-all border border-slate-200/60"
                        >
                          <div className="bg-gradient-to-br from-purple-300 to-purple-400 rounded-lg p-2 shadow-sm">
                            <Calendar className="h-5 w-5 text-white" />
                          </div>
                          <div>
                            <div className="font-bold text-slate-900">
                              Leave Requests
                            </div>
                            <div className="text-xs text-slate-600 font-medium">
                              View and manage leave requests
                            </div>
                          </div>
                        </button>

                        <button
                          onClick={() =>
                            handleViewDetails(selectedTeacherForManagement)
                          }
                          className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-medium bg-gradient-to-r from-slate-50 to-blue-50 hover:shadow-md rounded-xl transition-all border border-slate-200/60"
                        >
                          <div className="bg-gradient-to-br from-slate-300 to-slate-400 rounded-lg p-2 shadow-sm">
                            <Eye className="h-5 w-5 text-white" />
                          </div>
                          <div>
                            <div className="font-bold text-slate-900">
                              View Details
                            </div>
                            <div className="text-xs text-slate-600 font-medium">
                              View complete teacher information
                            </div>
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-slate-50 to-blue-50 px-6 py-4 sm:flex sm:flex-row-reverse border-t border-slate-200/60">
                <button
                  onClick={() => setIsManagementModalOpen(false)}
                  className="w-full inline-flex justify-center px-5 py-2.5 bg-white border-2 border-slate-200 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300 sm:ml-3 sm:w-auto"
                >
                  <span className="text-slate-700">Cancel</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Management Modals */}
      {selectedTeacherId && (
        <>
          <ClassAssignmentModal
            isOpen={isClassAssignmentModalOpen}
            onClose={() => {
              setIsClassAssignmentModalOpen(false);
              setSelectedTeacherId(null);
            }}
            teacherId={selectedTeacherId}
          />

          <StatusChangeModal
            isOpen={isStatusChangeModalOpen}
            onClose={() => {
              setIsStatusChangeModalOpen(false);
              setSelectedTeacherId(null);
            }}
            teacherId={selectedTeacherId}
          />

          <LeaveRequestsModal
            isOpen={isLeaveRequestModalOpen}
            onClose={() => {
              setIsLeaveRequestModalOpen(false);
              setSelectedTeacherId(null);
            }}
            teacherId={selectedTeacherId}
          />
        </>
      )}
    </div>
  );
};

export default AdminTeachers;
